/**
 * /tenants/$slug/authentication/sign-in — the Sign-in tab of the Authentication hub.
 *
 * Composes two sections that share one mental model ("how the sign-in ceremony
 * proceeds, and what happens when the primary path can't"):
 *   1. Sign-in methods + policy (AuthenticationSettingsPage — owns the sticky save bar).
 *   2. Recovery-fallback cascade order (CascadePolicySection — folded in from the former
 *      standalone "Fallback" tab during the 6→3 hub composition).
 * The loader prefetches the cascade policy so the second section renders warm.
 */

import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import { AuthenticationSettingsPage } from "@/features/tenant-settings/pages/authentication-settings-page";
import { CascadePolicySection } from "@/features/cascade-policy/pages/cascade-policy-page";
import { AuthPostureSummary } from "@/features/authentication-overview/components/auth-posture-summary";
import { fetchCascadePolicy } from "@/features/cascade-policy/queries";
import { fetchTenantDetail } from "@/features/tenants/queries";
import { queryKeys } from "@/lib/query-keys";
import { applicationAccessSearchSchema } from "@/features/ciam-applications/schemas";

export const Route = createFileRoute("/_authed/tenants/$slug/authentication/sign-in")({
  validateSearch: applicationAccessSearchSchema.parse,
  // The tenant DETAIL — not the light tenant context the $slug layout
  // carries. AuthenticationSettingsPage is a settings form: it reads
  // webauthn.attestation, support_email, customer_audience and, load-
  // bearingly, updated_at for the If-Unmodified-Since on every save.
  //
  // It comes from beforeLoad (route context), not a component query,
  // because useSettingsForm's save and discard paths both invalidate this
  // query key and then call router.invalidate() to pull the post-save
  // updated_at into context. Two consecutive saves race each other's
  // updated_at and the second returns 412 without that refresh.
  //
  // staleTime 0 matches the settings layout: a settings form must open on
  // the server's current row, never on a cached one.
  //
  // This page used to live under /tenants/$slug/settings and read the
  // detail from THAT layout's context. The 6->3 Authentication-hub
  // composition moved the page here and left the getRouteApi behind, so
  // every visit threw "Could not find an active match from
  // /_authed/tenants/$slug/settings" — the settings route is not an
  // ancestor of this one, and a route API only resolves against matches
  // that are actually active.
  beforeLoad: async ({ context }) => ({
    settingsTenant: await context.queryClient.fetchQuery({
      queryKey: queryKeys.tenants.detail(context.tenant.slug),
      queryFn: () => fetchTenantDetail(context.tenant.id),
      staleTime: 0,
    }),
  }),
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: queryKeys.cascadePolicy.policy(context.tenant.id),
      queryFn: () => fetchCascadePolicy(context.tenant.id),
    }),
  component: SignInTab,
});

const tenantRouteApi = getRouteApi("/_authed/tenants/$slug");

function SignInTab() {
  const { tenant } = tenantRouteApi.useRouteContext();
  return (
    <div className="space-y-8">
      <AuthPostureSummary slug={tenant.slug} tenantID={tenant.id} />
      <AuthenticationSettingsPage />
      <CascadePolicySection tenantID={tenant.id} />
    </div>
  );
}
